import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { postDataAPI } from '../../lib/api'
import Step01AutoSendOtp from './steps/Step01AutoSendOtp'
import Step02VerifyOtp from './steps/Step02VerifyOtp'
import Step03FindPresence from './steps/Step03FindPresence'
import Step04Username from './steps/Step04Username'
import Step06Avatar from './steps/Step06Avatar'
import Step07TeamJoin from './steps/Step07TeamJoin'
import Step08TeamUsername from './steps/Step08TeamUsername'
import OnboardingShell from './steps/OnboardingShell'
import { OnboardingStep } from './steps/steps'

export type SignupUser = {
  email?: string
  name?: string
  professionRole?: 'artist' | 'manager' | 'support' | string
  plan?: number
  teamMemberPlan?: string
  username?: string
  domain?: string
  avatar?: string | File | null
  social?: Record<string, string>
  topSongs?: any[]
  topVideo?: any
  pendingBandsintownImport?: string
  /** Canonical artist name from Spotify onboarding — source of truth across the platform */
  dizeeArtistName?: string
  /** Set when user landed with ?invited=1 (invited to a team) */
  invitedFromQuery?: boolean
  /** Token for "invite artist to Dizee" claim flow; set when ?type=artist-invite&token=... */
  artistInviteToken?: string
}

const STEP_VALIDATING_ARTIST_INVITE = 0

export default function SignupPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const query = useMemo(() => new URLSearchParams(location.search), [location.search])

  const emailFromQuery = query.get('email') || undefined
  const from = query.get('from') || '/'
  const invitedFromQuery = query.get('invited') === '1'
  const artistInviteType = query.get('type') === 'artist-invite'
  const artistInviteToken = query.get('token') || undefined

  const [step, setStep] = useState<number>(() =>
    artistInviteType && artistInviteToken ? STEP_VALIDATING_ARTIST_INVITE : OnboardingStep.AutoSendOtp
  )
  const [user, setUser] = useState<SignupUser>({
    email: emailFromQuery,
    invitedFromQuery,
    ...(artistInviteType && artistInviteToken ? { artistInviteToken } : {}),
  })
  const [validateError, setValidateError] = useState<string | null>(null)

  // Redirect to login only when we have no email and we're not in artist-invite flow (which will set email after validate)
  useEffect(() => {
    if (user.email) return
    if (artistInviteType && artistInviteToken) return
    navigate('/login', { replace: true })
  }, [user.email, artistInviteType, artistInviteToken, navigate])

  // Artist-invite: validate token and set email/name
  useEffect(() => {
    if (step !== STEP_VALIDATING_ARTIST_INVITE || !artistInviteToken) return
    let cancelled = false
    setValidateError(null)
    postDataAPI('team/validate-artist-invite', { token: artistInviteToken })
      .then((res: any) => {
        if (cancelled) return
        const data = res?.data ?? res
        const email = data?.email
        const name = data?.name
        if (email) {
          setUser((prev) => ({ ...prev, email, name: name || prev.name, artistInviteToken }))
          setStep(OnboardingStep.AutoSendOtp)
        } else {
          setValidateError('Invalid or expired invitation')
        }
      })
      .catch((err: any) => {
        if (cancelled) return
        const msg = err?.response?.data?.message ?? err?.message ?? 'Invalid or expired invitation'
        setValidateError(msg)
      })
    return () => { cancelled = true }
  }, [step, artistInviteToken])

  useEffect(() => {
    if (emailFromQuery) setUser((prev) => ({ ...prev, email: emailFromQuery, invitedFromQuery }))
  }, [emailFromQuery, invitedFromQuery])

  // If already authed, skip signup — except while finishing team invite (account exists but join-artist not done yet).
  useEffect(() => {
    const token = localStorage.getItem('dizeeToken')
    if (!token) return
    if (sessionStorage.getItem('signupTeamJoinPending') === '1') return
    navigate(from, { replace: true })
  }, [from, navigate])

  if (step === STEP_VALIDATING_ARTIST_INVITE) {
    return (
      <OnboardingShell title="Validating invitation…" footer="You're invited to join Dizee as an artist.">
        {validateError ? (
          <div className="space-y-4">
            <p className="text-sm text-red-600 dark:text-red-400">{validateError}</p>
            <a href="/login" className="ds-btn ds-btn-primary inline-block">
              Go to login
            </a>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">Please wait…</p>
        )}
      </OnboardingShell>
    )
  }

  return (
    <div className="min-h-screen">
      {step === OnboardingStep.AutoSendOtp && <Step01AutoSendOtp setStep={setStep} user={user} setUser={setUser} />}
      {step === OnboardingStep.VerifyOtp && <Step02VerifyOtp setStep={setStep} user={user} setUser={setUser} />}
      {step === OnboardingStep.FindPresence && <Step03FindPresence setStep={setStep} user={user} setUser={setUser} />}
      {step === OnboardingStep.Username && <Step04Username setStep={setStep} user={user} setUser={setUser} />}
      {step === OnboardingStep.Avatar && <Step06Avatar setStep={setStep} user={user} setUser={setUser} from={from} />}
      {step === OnboardingStep.TeamJoin && <Step07TeamJoin setStep={setStep} user={user} setUser={setUser} />}
      {step === OnboardingStep.TeamUsername && <Step08TeamUsername setStep={setStep} user={user} setUser={setUser} />}
    </div>
  )
}

